import React, {memo, ReactNode} from 'react';
import {  MediaModel, AirConditionerState} from '../../../types/control';
import './index.scss';

import airConditionerActive from '../../../assets/images/icons/controls/air-conditioner-open.png';
import airConditionerPassive from '../../../assets/images/icons/controls/air-conditioner-close.png';

import fan0 from '../../../assets/images/icons/controls/fan-0.png';
import fan1 from '../../../assets/images/icons/controls/fan-1.png';
import fan2 from '../../../assets/images/icons/controls/fan-2.png';
import fan3 from '../../../assets/images/icons/controls/fan-3.png';
import fan4 from '../../../assets/images/icons/controls/fan-4.png';

import flap0 from '../../../assets/images/icons/controls/air-flap-0.png';
import flap1 from '../../../assets/images/icons/controls/air-flap-1.png';
import flap2 from '../../../assets/images/icons/controls/air-flap-2.png';
import flap3 from '../../../assets/images/icons/controls/air-flap-3.png';
import flap4 from '../../../assets/images/icons/controls/air-flap-4.png';

import {Column} from '../../Column';
import {Widget} from '../../Widget';

import iconOff from "../../../assets/images/icons/off-icon.png"
import iconOn from "../../../assets/images/icons/on-icon.png"
import iconOnOff from '../../../assets/images/icons/controls/on-off.png';
import iconPlus from '../../../assets/images/icons/controls/air-conditioner/plus.png';
import iconMinus from '../../../assets/images/icons/controls/air-conditioner/minus.png';
import iconVolume from '../../../assets/images/icons/controls/volume.png';
import iconMute from '../../../assets/images/icons/controls/mute.png';
import iconPlay from '../../../assets/images/icons/controls/play.png';
import iconProgram from '../../../assets/images/icons/controls/program.png';
import iconOther from '../../../assets/images/icons/media/other.png';
import iconAirConditioner from '../../../assets/images/icons/media/air-conditioner.png';
import iconTv from '../../../assets/images/icons/media/tv.png';
import iconMusicPlayer from '../../../assets/images/icons/media/music-player.png';
import iconSatellite from '../../../assets/images/icons/media/satellite.png';
import iconProjection from '../../../assets/images/icons/media/projection.png';
import {onClickHandler} from '../../../utils/onclick';
import minusIcon from "../../../assets/images/icons/controls/minus.png"
import plusIcon from "../../../assets/images/icons/controls/plus.png"

const fans = [fan0, fan1, fan2, fan3, fan4];
const flaps = [flap0, flap1, flap2, flap3, flap4];

type MediaDeviceProps<T = unknown> = {
    media: MediaModel<T>;
    children?: ReactNode;
};

type OtherMediaDeviceProps = {
    click: () => void;
    mute: () => void;
    play: () => void;
    minus: () => void;
    plus: () => void;
    volumeMinus: () => void;
    volumePlus: () => void;

} & MediaDeviceProps;

type AirConditionerProps = {
    click: (isOpen?: boolean) => void;
    minus: () => void;
    plus: () => void;
    fanMinus: () => void;
    fanPlus: () => void;
    airFlapMinus: () => void;
    airFlapPlus: () => void;
} & MediaDeviceProps<AirConditionerState>;

const MyButton = memo(function MyButton({icon, onClick}: { icon: string; onClick: () => void }) {
    return (
        <div className="button" {...onClickHandler(onClick)}>
            <img src={icon} alt=""/>
        </div>
    );
});

const NoBorder = memo(function NoBorder({icon, text}: { icon?: string; text?: string }) {
    if (icon == null && text) {
        return (
            <div className="button no-border">
                <div className="temperature">
                    <span>{text}<small>°</small></span>
                </div>
            </div>
        );
    } else if (icon && !text) {
        return (
            <div className="button no-border">
                <img src={icon} alt=""/>
            </div>
        );
    } else {
        return (
            <div className="button no-border"/>
        );
    }
});

export const AirConditionerControlComponent = memo(function AirConditionerControlComponent(props: AirConditionerProps) {
    const {media, children, click, minus, plus, fanMinus, fanPlus, airFlapMinus, airFlapPlus} = props;

    const activeIcon = media.state.open ? airConditionerActive : airConditionerPassive;

    return (
        <Column min={6} max={12}>
            <Widget>
                <div className="position-relative">
                    <div className="air-wrapper">
                        <div>
                            <div className="left">
                                <div className="open-close-button">
                                    <img src={activeIcon} alt='icon'/>
                                    <div className="open-close-button-title">
                                        <span>{media.name}</span>
                                    </div>
                                </div>

                                <div className="left-bottom">
                                    <MyButton icon={iconOff} onClick={() => click(false)}/>
                                    <MyButton icon={iconOn} onClick={() => click(true)}/>
                                </div>
                            </div>
                            <div className="right">
                                <div>
                                    <NoBorder text={media.state.temperature.toFixed(0)}/>
                                    <NoBorder icon={fans[media.state.fan_speed]}/>
                                    <NoBorder icon={flaps[media.state.flap_speed]}/>
                                </div>
                                <div>
                                    <MyButton icon={minusIcon} onClick={minus}/>
                                    <MyButton icon={minusIcon} onClick={fanMinus}/>
                                    <MyButton icon={minusIcon} onClick={airFlapMinus}/>
                                </div>
                                <div>
                                    <MyButton icon={plusIcon} onClick={plus}/>
                                    <MyButton icon={plusIcon} onClick={fanPlus}/>
                                    <MyButton icon={plusIcon} onClick={airFlapPlus}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    {children}
                </div>
            </Widget>
        </Column>
    );
});

export const MediaOtherControl = memo(function MediaOtherControl(props: OtherMediaDeviceProps) {
    const {children, media, click, mute, play, minus, plus, volumeMinus, volumePlus} = props;

    let icon = iconOther;
    switch (media.icon) {
        case 'air-conditioner':
            icon = iconAirConditioner;
            break;
        case 'tv':
            icon = iconTv;
            break;
        case 'music-player':
            icon = iconMusicPlayer;
            break;
        case 'satellite':
            icon = iconSatellite;
            break;
        case 'projection':
            icon = iconProjection;
            break;
    }

    return (
        <Column min={6} max={12}>
            <Widget>
                <div className="position-relative">
                    <div className="air-wrapper">
                        <div>
                            <div className="left">
                                <div className="open-close-button">
                                    <img src={icon} alt='icon'/>
                                    <div className="open-close-button-title">
                                        <span>{media.name}</span>
                                    </div>
                                </div>
                                <div className="left-bottom">
                                    <MyButton icon={iconOnOff} onClick={click}/>
                                </div>
                            </div>
                            <div className="right">
                                <div>
                                    <NoBorder/>
                                    <NoBorder icon={iconProgram}/>
                                    <NoBorder icon={iconVolume}/>
                                </div>
                                <div>
                                    <MyButton icon={iconMute} onClick={mute}/>
                                    <MyButton icon={iconMinus} onClick={minus}/>
                                    <MyButton icon={iconMinus} onClick={volumeMinus}/>
                                </div>
                                <div>
                                    <MyButton icon={iconPlay} onClick={play}/>
                                    <MyButton icon={iconPlus} onClick={plus}/>
                                    <MyButton icon={iconPlus} onClick={volumePlus}/>
                                </div>
                            </div>
                        </div>
                    </div>
                    {children}
                </div>
            </Widget>
        </Column>
    );
});
